import { create } from "zustand";
import { persist } from "zustand/middleware";
import useModalStore from "@/store/use-modal-store";
import React from "react";

// ===== Settings Types =====
export type SettingsTab =
  | "profile"
  | "security"
  | "appearance"
  | "billing"
  | "usage"
  | "permissions"
  | "rewards";

interface SettingsState {
  // Tab Management
  activeTab: SettingsTab;

  // Form States (for unsaved changes tracking)
  hasUnsavedChanges: boolean;
  unsavedTabs: Set<SettingsTab>;

  // Loading States
  isLoading: boolean;
  loadingTabs: Set<SettingsTab>;

  // Draft Reset Registry (tab-agnostic)
  draftResetters: Set<() => void>;
}

interface SettingsActions {
  // Tab Actions
  setActiveTab: (tab: SettingsTab) => void;

  // Form State Actions
  setHasUnsavedChanges: (tab: SettingsTab, hasChanges: boolean) => void;
  markTabAsSaved: (tab: SettingsTab) => void;
  resetUnsavedChanges: () => void;

  // Loading Actions
  setTabLoading: (tab: SettingsTab, isLoading: boolean) => void;
  setGlobalLoading: (isLoading: boolean) => void;

  // Reset Actions
  resetSettings: () => void;

  // Draft Reset Registry Actions
  registerDraftReset: (resetter: () => void) => void;
  unregisterDraftReset: (resetter: () => void) => void;
  clearAllDrafts: () => void;
}

// ===== Initial State =====
const initialState: SettingsState = {
  activeTab: "profile",
  hasUnsavedChanges: false,
  unsavedTabs: new Set(),
  isLoading: false,
  loadingTabs: new Set(),
  draftResetters: new Set(),
};

// ===== Settings Store =====
export const useSettingsStore = create<SettingsState & SettingsActions>()(
  persist(
    (set, get) => ({
      // ===== State =====
      ...initialState,

      // ===== Tab Actions =====
      setActiveTab: (tab: SettingsTab) => {
        set({ activeTab: tab });
      },

      // ===== Form State Actions =====
      setHasUnsavedChanges: (tab: SettingsTab, hasChanges: boolean) => {
        const { unsavedTabs } = get();
        const newUnsavedTabs = new Set(unsavedTabs);

        if (hasChanges) {
          newUnsavedTabs.add(tab);
        } else {
          newUnsavedTabs.delete(tab);
        }

        set({
          unsavedTabs: newUnsavedTabs,
          hasUnsavedChanges: newUnsavedTabs.size > 0,
        });
      },

      markTabAsSaved: (tab: SettingsTab) => {
        const { unsavedTabs } = get();
        const newUnsavedTabs = new Set(unsavedTabs);
        newUnsavedTabs.delete(tab);

        set({
          unsavedTabs: newUnsavedTabs,
          hasUnsavedChanges: newUnsavedTabs.size > 0,
        });
      },

      resetUnsavedChanges: () => {
        set({
          hasUnsavedChanges: false,
          unsavedTabs: new Set(),
        });
      },

      // ===== Loading Actions =====
      setTabLoading: (tab: SettingsTab, isLoading: boolean) => {
        const { loadingTabs } = get();
        const newLoadingTabs = new Set(loadingTabs);

        if (isLoading) {
          newLoadingTabs.add(tab);
        } else {
          newLoadingTabs.delete(tab);
        }

        set({
          loadingTabs: newLoadingTabs,
          isLoading: newLoadingTabs.size > 0,
        });
      },

      setGlobalLoading: (isLoading: boolean) => {
        set({ isLoading });
      },

      // ===== Reset Actions =====
      resetSettings: () => {
        set({
          ...initialState,
          unsavedTabs: new Set(),
          loadingTabs: new Set(),
        });
      },

      // ===== Draft Reset Registry Actions =====
      registerDraftReset: (resetter: () => void) => {
        const { draftResetters } = get();
        const next = new Set(draftResetters);
        next.add(resetter);
        set({ draftResetters: next });
      },

      unregisterDraftReset: (resetter: () => void) => {
        const { draftResetters } = get();
        const next = new Set(draftResetters);
        next.delete(resetter);
        set({ draftResetters: next });
      },

      clearAllDrafts: () => {
        const { draftResetters } = get();
        // Tüm kayıtlı reset fonksiyonlarını çağır
        draftResetters.forEach((reset) => {
          try {
            reset();
          } catch (_e) {
            // no-op: reset sırasında hata olsa bile akışı engelleme
          }
        });
        // Unsaved flags'i temizle
        set({ hasUnsavedChanges: false, unsavedTabs: new Set() });
      },
    }),
    {
      name: "settings-storage",
      version: 2,
      migrate: (persistedState: any, version: number) => {
        // v1 -> v2: notifications tab ismi permissions olarak değiştirildi
        if (version < 2 && persistedState?.activeTab === "notifications") {
          return { ...persistedState, activeTab: "permissions" };
        }
        return persistedState;
      },
      partialize: () => ({
        // activeTab URL'den okunduğu için persist edilmiyor
      }),
    }
  )
);

// ===== Convenience Hooks =====

// Tab Management Hook
export const useSettingsTab = () => {
  const { activeTab, setActiveTab } = useSettingsStore();
  return { activeTab, setActiveTab };
};

// Form State Hook
export const useSettingsFormState = (tab: SettingsTab) => {
  const {
    unsavedTabs,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    markTabAsSaved,
    registerDraftReset,
    unregisterDraftReset,
  } = useSettingsStore();

  const hasTabUnsavedChanges = unsavedTabs.has(tab);

  // Memoize the setHasUnsavedChanges function to prevent infinite loops
  const memoizedSetHasUnsavedChanges = React.useCallback(
    (hasChanges: boolean) => setHasUnsavedChanges(tab, hasChanges),
    [tab, setHasUnsavedChanges]
  );

  const memoizedMarkAsSaved = React.useCallback(
    () => markTabAsSaved(tab),
    [tab, markTabAsSaved]
  );

  return {
    hasUnsavedChanges: hasTabUnsavedChanges,
    hasAnyUnsavedChanges: hasUnsavedChanges,
    setHasUnsavedChanges: memoizedSetHasUnsavedChanges,
    markAsSaved: memoizedMarkAsSaved,
    registerDraftReset,
    unregisterDraftReset,
  };
};

// Loading State Hook
export const useSettingsLoading = (tab?: SettingsTab) => {
  const { isLoading, loadingTabs, setTabLoading, setGlobalLoading } =
    useSettingsStore();

  const isTabLoading = tab ? loadingTabs.has(tab) : false;

  return {
    isLoading,
    isTabLoading,
    setTabLoading: tab
      ? (loading: boolean) => setTabLoading(tab, loading)
      : undefined,
    setGlobalLoading,
  };
};

// Settings Navigation Hook (with unsaved changes warning)
export const useSettingsNavigation = () => {
  const {
    activeTab,
    setActiveTab,
    hasUnsavedChanges,
    unsavedTabs,
    markTabAsSaved,
    clearAllDrafts,
  } = useSettingsStore();

  const navigateToTab = (tab: SettingsTab, force = false) => {
    if (!force && hasUnsavedChanges) {
      // Use modal system instead of window.confirm
      useModalStore.getState().openUnsavedChanges(() => {
        // 1) Tüm draft'ları temizle (genel çözüm)
        clearAllDrafts();
        setActiveTab(tab);
        // 2) Navigated tab dahil tüm unsaved işaretlerini temizle
        // markTabAsSaved yerine global temizliği yukarıda yaptık
        // yine de güvenlik için hedef tab'i saved işaretleyelim
        markTabAsSaved(tab);
      });
      return false;
    }

    setActiveTab(tab);
    return true;
  };

  return {
    activeTab,
    hasUnsavedChanges,
    unsavedTabs,
    navigateToTab,
    setActiveTab: (tab: SettingsTab) => navigateToTab(tab, false),
    forceNavigateToTab: (tab: SettingsTab) => navigateToTab(tab, true),
  };
};

export default useSettingsStore;
